import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  Gauge, Database, FileText, Settings as SettingsIcon, Sparkles, LogOut,
  Moon, Sun, Activity, Building2, Wallet, HeartPulse, GraduationCap, Ban, Layers,
  Wind, Users, LayoutGrid, ShieldAlert, ChevronDown, ChevronRight, Briefcase,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ROLE_LABELS } from "../lib/utils";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { AIAssistant } from "./AIAssistant";
import { NotificationBell } from "./NotificationBell";
import Footer from "./Footer";

// Single navigation model (S-012): everything lives in the sidebar. Items are
// either direct links or collapsible groups — there is no separate top tab bar.
const NAV = [
  { type: "link", label: "Home", to: "/", icon: Gauge, testid: "nav-home" },
  { type: "link", label: "Environment", to: "/environment", icon: Wind, testid: "nav-environment" },
  { type: "link", label: "Social & Governance", to: "/social-gov", icon: Users, testid: "nav-social-gov" },
  { type: "link", label: "Operational Efficiency", to: "/operational-efficiency", icon: Activity, testid: "nav-operational-efficiency" },
  { type: "link", label: "Materiality", to: "/materiality", icon: Layers, testid: "nav-materiality" },
  { type: "link", label: "Custom KPIs", to: "/custom-kpis", icon: LayoutGrid, testid: "nav-custom-kpis" },
  { type: "link", label: "Score Intelligence", to: "/score-intelligence", icon: Gauge, testid: "nav-score-intelligence" },
  {
    type: "group", key: "reporting", label: "Reporting & Compliance", icon: FileText, testid: "nav-reporting",
    tabs: [
      { label: "Overview", to: "/reports", testid: "tab-reports" },
      { label: "Sustainability Report", to: "/sustainability-report", testid: "tab-sustainability-report" },
      { label: "Report Studio", to: "/report-studio", testid: "tab-report-studio" },
      { label: "Local", to: "/reporting/local", testid: "tab-reporting-local" },
      { label: "International", to: "/reporting/international", testid: "tab-reporting-international" },
      { label: "Customer Compliance", to: "/reporting/customer-compliance", testid: "tab-customer-compliance" },
      { label: "Filings", to: "/filings", testid: "tab-filings" },
      { label: "CBAM", to: "/reporting/cbam", testid: "tab-reporting-cbam" },
      { label: "Project Gallery", to: "/gallery", testid: "tab-gallery" },
    ],
  },
  {
    type: "group", key: "scenarios", label: "Scenarios & Risks", icon: ShieldAlert, testid: "nav-scenarios",
    tabs: [
      { label: "Scenarios & Risks", to: "/scenarios", testid: "tab-scenarios" },
      { label: "My Operations (Climate)", to: "/climate-operations", testid: "tab-climate-operations" },
      { label: "Risk Register", to: "/risk-register", testid: "tab-risk-register" },
      { label: "Hazards & Locations", to: "/hazard-risk", testid: "tab-hazard-risk" },
      { label: "Investment Register", to: "/initiatives", testid: "tab-initiatives" },
      { label: "ESG Scorecard", to: "/esg-scorecard", testid: "tab-esg-scorecard" },
    ],
  },
  { type: "link", label: "Supply Chain", to: "/suppliers", icon: Building2, testid: "nav-supply-chain" },
  {
    type: "group", key: "finance", label: "Finance & Offset", icon: Wallet, testid: "nav-finance",
    tabs: [
      { label: "Finance & Offset", to: "/finance-offset", testid: "tab-finance-offset" },
      { label: "Green Financing", to: "/financing", testid: "tab-financing" },
    ],
  },
  {
    type: "group", key: "data", label: "Data Management", icon: Database, testid: "nav-data",
    tabs: [
      { label: "Data Management", to: "/data-management", testid: "tab-data-management" },
      { label: "GHG Inventory", to: "/ghg-inventory", testid: "tab-ghg-inventory" },
      { label: "Data Flow Map", to: "/data-flow", testid: "tab-data-flow" },
      { label: "Baseline Year", to: "/baseline", testid: "tab-baseline" },
      { label: "Mobile Combustion", to: "/mobile-combustion", testid: "tab-mobile-combustion" },
      { label: "Data Collection Templates", to: "/data", testid: "tab-data" },
      { label: "Document Inbox", to: "/documents", testid: "tab-documents" },
      { label: "Anomalies", to: "/anomalies", testid: "tab-anomalies" },
      { label: "AI Reconciliation", to: "/reconciliation", testid: "tab-reconciliation" },
      { label: "QA Findings", to: "/qa-findings", testid: "tab-qa-findings" },
      { label: "AI Use Transparency", to: "/ai-transparency", testid: "tab-ai-transparency" },
    ],
  },
  {
    type: "group", key: "settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings",
    tabs: [
      { label: "Settings", to: "/settings", testid: "tab-settings" },
      { label: "Lestari ValuePrint", to: "/valueprint", testid: "tab-valueprint" },
      { label: "Billing & Usage", to: "/billing", testid: "tab-billing" },
      { label: "Setup Centre", to: "/setup", testid: "tab-setup" },
      { label: "Connections", to: "/connectors", testid: "tab-connectors" },
      { label: "SEFR & Calculator", to: "/emissions", testid: "tab-emissions" },
    ],
  },
];

const tabMatches = (to, pathname) =>
  to === "/" ? pathname === "/" : (pathname === to || pathname.startsWith(to + "/"));

const itemActive = (item, pathname) =>
  item.type === "link" ? tabMatches(item.to, pathname) : item.tabs.some((t) => tabMatches(t.to, pathname));

// Platform operator (super_admin) navigation — fleet health & tenant lifecycle only.
const PLATFORM_NAV = [
  {
    type: "group", key: "platform-health", label: "Platform Health", icon: HeartPulse, testid: "nav-platform-health",
    tabs: [
      { label: "Platform Health", to: "/admin/health", testid: "tab-platform-health" },
      { label: "Operator Audit Log", to: "/admin/audit", testid: "tab-platform-audit" },
      { label: "Email Deliverability", to: "/admin/email", testid: "tab-platform-email" },
    ],
  },
  {
    type: "group", key: "platform-tenants", label: "Tenants", icon: Building2, testid: "nav-platform-tenants",
    tabs: [
      { label: "Tenant Console", to: "/admin/tenants", testid: "tab-platform-tenants" },
      { label: "Announcements", to: "/admin/announcements", testid: "tab-platform-announcements" },
    ],
  },
  {
    type: "group", key: "platform-billing", label: "Billing & Plans", icon: Wallet, testid: "nav-platform-billing",
    tabs: [
      { label: "Billing Overview", to: "/admin/billing", testid: "tab-platform-billing" },
      { label: "Plans & Pricing", to: "/admin/plans", testid: "tab-platform-plans" },
      { label: "Invoices", to: "/admin/invoices", testid: "tab-platform-invoices" },
      { label: "Accounting", to: "/admin/accounting", testid: "tab-platform-accounting" },
    ],
  },
  {
    type: "group", key: "platform-services", label: "Professional Services", icon: Briefcase, testid: "nav-platform-services",
    tabs: [
      { label: "Service Catalogue", to: "/admin/services", testid: "tab-platform-services" },
      { label: "Engagements & Quotes", to: "/admin/service-engagements", testid: "tab-platform-service-engagements" },
    ],
  },
  {
    type: "group", key: "platform-templates", label: "Template Studio", icon: Layers, testid: "nav-platform-templates",
    tabs: [
      { label: "ValuePrint Templates", to: "/admin/templates", testid: "tab-platform-templates" },
      { label: "Hazard Catalog", to: "/admin/hazards", testid: "tab-platform-hazards" },
      { label: "GHG Catalog", to: "/admin/ghg-catalog", testid: "tab-platform-ghg-catalog" },
      { label: "Emission Factors", to: "/admin/emission-factors", testid: "tab-platform-emission-factors" },
      { label: "Country Locale Master", to: "/admin/locale", testid: "tab-platform-locale" },
      { label: "Singpass Registration", to: "/admin/singpass", testid: "tab-platform-singpass" },
    ],
  },
  {
    type: "group", key: "platform-training", label: "Training Media", icon: GraduationCap, testid: "nav-platform-training",
    tabs: [
      { label: "Training Media", to: "/admin/training", testid: "tab-platform-training" },
      { label: "Media Repository", to: "/admin/media", testid: "tab-platform-media" },
    ],
  },
];

const LINK_BASE = "flex items-center gap-3 px-3 py-2 rounded-sm text-[13px] font-medium transition-colors";

function NavGroup({ item, pathname }) {
  const isActive = item.tabs.some((t) => tabMatches(t.to, pathname));
  const [open, setOpen] = useState(isActive);
  useEffect(() => { if (isActive) setOpen(true); }, [isActive]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={item.testid}
        aria-expanded={open}
        className={cn(
          LINK_BASE, "w-full",
          isActive ? "text-accent" : "text-foreground hover:bg-surface-hover"
        )}
      >
        <item.icon className="h-4 w-4" strokeWidth={2} />
        <span className="flex-1 text-left">{item.label}</span>
        {open ? <ChevronDown className="h-3.5 w-3.5 opacity-60" /> : <ChevronRight className="h-3.5 w-3.5 opacity-60" />}
      </button>
      {open && (
        <div className="ml-4 pl-3 border-l border-border mt-0.5 space-y-0.5" data-testid={`${item.testid}-subnav`}>
          {item.tabs.map((t) => {
            const active = tabMatches(t.to, pathname);
            return (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={t.testid}
                className={cn(
                  "block px-3 py-1.5 rounded-sm text-[12.5px] transition-colors",
                  active ? "bg-accent text-accent-foreground font-medium" : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"
                )}
              >
                {t.label}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, tenant, logout } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const [aiOpen, setAiOpen] = useState(false);
  const isSuperAdmin = user?.role === "super_admin";
  const navItems = isSuperAdmin ? PLATFORM_NAV : NAV;

  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem("lestari_theme");
    if (stored) return stored === "dark";
    return !["data_officer"].includes(user?.role);
  });
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("lestari_theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-surface flex flex-col" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-border flex items-center gap-3 relative">
          <div className="h-9 w-9 bg-[#F2F4F3] flex items-center justify-center rounded-md p-1" data-testid="logo">
            <img src="/brand/logo_icon.png" alt="Lestari" className="h-full object-contain" />
          </div>
          <div>
            <div className="font-bold tracking-tight text-lg leading-tight" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>Lestari</div>
            <div className="text-[8px] uppercase tracking-[0.18em] text-muted-foreground">{isSuperAdmin ? "Platform Operations" : "Sustainability · Intelligence · Impact"}</div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: "linear-gradient(90deg, #117A7A, #D4A84C 70%, transparent)" }} />
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto scrollbar-thin">
          {navItems.map((item) => {
            if (item.type === "link") {
              const active = itemActive(item, pathname);
              return (
                <Link
                  key={item.testid}
                  to={item.to}
                  data-testid={item.testid}
                  className={cn(
                    LINK_BASE,
                    active ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-surface-hover"
                  )}
                >
                  <item.icon className="h-4 w-4" strokeWidth={2} />
                  {item.label}
                </Link>
              );
            }
            return <NavGroup key={item.key} item={item} pathname={pathname} />;
          })}
        </nav>

        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 bg-foreground/10 flex items-center justify-center rounded-sm border border-border">
              <span className="font-mono text-xs font-bold">{(user?.name || "U").slice(0, 2).toUpperCase()}</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground" data-testid="user-role">
                {ROLE_LABELS[user?.role] || user?.role}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start text-xs" onClick={logout} data-testid="logout-btn">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card flex items-center justify-between px-6">
          <div className="flex items-center gap-4 min-w-0">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{isSuperAdmin ? "Platform" : "Tenant"}</div>
              <div className="font-display font-semibold text-sm truncate" data-testid="tenant-name">
                {isSuperAdmin ? "Lestari Operations Console" : tenant?.name || "—"}
              </div>
            </div>
            {tenant?.regulatory_tier && (
              <Badge variant="outline" data-testid="tenant-tier">{tenant.regulatory_tier}</Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle" aria-label="Toggle theme">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            {!isSuperAdmin && <NotificationBell />}
            {!isSuperAdmin && (
              <Button variant="primary" size="sm" onClick={() => setAiOpen(true)} data-testid="open-ai-assistant-btn" className="gap-2">
                <Sparkles className="h-3.5 w-3.5" /> AI Assistant
              </Button>
            )}
          </div>
        </header>

        {!isSuperAdmin && tenant?.suspended && (
          <div className="bg-red-500/15 border-b border-red-500/40 text-red-400 text-xs px-6 py-2 flex items-center gap-2" data-testid="suspended-banner">
            <Ban className="h-3.5 w-3.5 shrink-0" />
            Your organisation has been suspended by the platform operator — the workspace is read-only. Contact Lestari support to resolve this.
          </div>
        )}

        <main className="flex-1 overflow-auto" data-testid="main-content">
          {children}
        </main>
        <Footer />
      </div>

      <AIAssistant open={aiOpen} onOpenChange={setAiOpen} />
    </div>
  );
}
